import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';

import { AccountClientService } from '@mode/shared/data-access-webapp';
import * as UserSelectors from './user.selectors';
import * as UserActions from './user.actions';
import { AvatarTypes } from '@mode/shared/contract-common';

@Injectable()
export class UserEffects {
  loadCurrentUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadCurrentUser),
      withLatestFrom(this.store.select(UserSelectors.selectSelf)),
      filter(([action, current]) => action.username !== current?.username), // don't attempt to reload the same user
      switchMap(([action]) => {
        return this.client.getAccount(action.username);
      }),
      map((user) => UserActions.loadCurrentUserSuccess({ user })),
      catchError((error) => of(UserActions.loadCurrentUserFailure({ error })))
    );
  });

  loadAnonymousUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadAnonymousUser),
      withLatestFrom(this.store.select(UserSelectors.selectSelf)),
      filter(([action, current]) => current == null || !current.isAnonymous), // don't attempt to reload the same user
      map(() =>
        UserActions.loadCurrentUserSuccess({
          user: {
            avatar: {
              type: AvatarTypes.AvatarType.Placeholder,
            },
            displayName: 'Anonymous',
            username: undefined,
            token: undefined,
            isAnonymous: true,
          },
        })
      )
    );
  });

  loadUserOrgs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadUserOrgs),
      switchMap((action) => this.client.getUserOrgs(action.username)),
      map((orgs) =>
        UserActions.loadUserOrgsSuccess({
          orgs,
        })
      ),
      catchError((error) => of(UserActions.loadUserOrgsFailure({ error })))
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store,
    private client: AccountClientService
  ) {}
}
